/**
 * ApproximatePositionProvider (#527) — one city-level fix from the network,
 * so the tracker opens on a map without asking the browser for location.
 *
 * `start()` fetches `config.locate.url` (CloudFront viewer geo behind the
 * briefer host) and emits ONE `GeolocationPosition`-shaped fix, always
 * asynchronously, with `accuracy` set to what the source is worth and no
 * altitude, heading or speed. It never emits again: `fixesAge` is false,
 * so the loop neither ages the fix nor refetches on its timer.
 *
 * A failed request is retried (`config.locate.retryMs`, up to
 * `maxAttempts`) while the source stays armed — the loop marks itself
 * running once `start()` returns and would never call it again, so the
 * retry has to live here. The final failure is reported through `onError`
 * as POSITION_UNAVAILABLE, which the loop escalates like any source, so the
 * placeholder can say so. No fix is ever fabricated.
 *
 * `start()` returns `true` as soon as the request is in flight: a
 * synchronous fix would be dropped (PositionProvider contract).
 */
import { config } from '@/config/config';
import type { PositionProvider, PositionProviderKind } from './PositionProvider';

interface LocateResponse {
  country?: string;
  location?: { latitude?: string | number; longitude?: string | number };
}

type OnPosition = (pos: GeolocationPosition) => void;
type OnError = (err: GeolocationPositionError) => void;

export class ApproximatePositionProvider implements PositionProvider {
  readonly kind: PositionProviderKind = 'approximate';
  readonly fixesAge = false;

  private _armed = false;
  private _controller: AbortController | null = null;
  private _retryTimer: ReturnType<typeof setTimeout> | null = null;
  private _attempts = 0;

  start(onPosition: OnPosition, onError: OnError): boolean {
    if (this._armed) return true;
    this._armed = true;
    this._attempts = 0;
    this._request(onPosition, onError);
    return true;
  }

  stop(): void {
    this._armed = false;
    if (this._controller !== null) {
      this._controller.abort();
      this._controller = null;
    }
    if (this._retryTimer !== null) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
  }

  private _request(onPosition: OnPosition, onError: OnError): void {
    const controller = new AbortController();
    this._controller = controller;
    this._attempts += 1;
    const timer = setTimeout(() => controller.abort(), config.locate.timeoutMs);

    void (async () => {
      try {
        const res = await fetch(config.locate.url, { signal: controller.signal });
        if (!res.ok) return this._fail(controller, 'http_error', { status: res.status }, onPosition, onError);
        let body: LocateResponse;
        try {
          body = (await res.json()) as LocateResponse;
        } catch {
          // A captive portal or an edge page served with 200 (PR #528 review).
          return this._fail(controller, 'malformed_body', { status: res.status }, onPosition, onError);
        }
        const lat = Number(body.location?.latitude);
        const lon = Number(body.location?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
          return this._fail(controller, 'malformed_body', {}, onPosition, onError);
        }
        // Still THIS request, and still wanted? A stop() (or a stop/start pair)
        // in the meantime means nobody is listening to this one.
        if (!this._current(controller)) return;
        this._controller = null;
        onPosition(approximateFix(lat, lon, config.locate.accuracyM));
      } catch (err) {
        if (!this._current(controller)) return; // aborted by stop(): silence
        this._fail(controller, 'network_or_timeout', { name: (err as Error)?.name }, onPosition, onError);
      } finally {
        clearTimeout(timer);
      }
    })();
  }

  /** The request that owns `controller` is the one the provider is still waiting on. */
  private _current(controller: AbortController): boolean {
    return this._armed && this._controller === controller && !controller.signal.aborted;
  }

  private _fail(
    controller: AbortController,
    cause: string,
    detail: Record<string, unknown>,
    onPosition: OnPosition,
    onError: OnError
  ): void {
    if (!this._current(controller)) return;
    this._controller = null;
    console.warn(JSON.stringify({ event: 'approximate_position_unavailable', cause, attempt: this._attempts, ...detail }));
    if (this._attempts < config.locate.maxAttempts) {
      this._retryTimer = setTimeout(() => {
        this._retryTimer = null;
        if (this._armed) this._request(onPosition, onError);
      }, config.locate.retryMs);
      return;
    }
    onError(unavailable(cause));
  }
}

/** The browser's shape, so everything downstream of the seam is unchanged. */
export function approximateFix(lat: number, lon: number, accuracyM: number): GeolocationPosition {
  const coords = {
    latitude: lat,
    longitude: lon,
    accuracy: accuracyM,
    altitude: null,
    altitudeAccuracy: null,
    heading: null,
    speed: null,
    toJSON() { return { ...this }; }
  } as GeolocationCoordinates;
  return { coords, timestamp: Date.now(), toJSON() { return { coords, timestamp: this.timestamp }; } } as GeolocationPosition;
}

function unavailable(cause: string): GeolocationPositionError {
  return {
    code: 2,
    message: `approximate position unavailable: ${cause}`,
    PERMISSION_DENIED: 1,
    POSITION_UNAVAILABLE: 2,
    TIMEOUT: 3
  } as GeolocationPositionError;
}
